import React, { createContext, useContext, useState, useCallback } from 'react';

interface Notification {
  id: string;
  type: 'success' | 'error' | 'info' | 'warning';
  title: string;
  message?: string;
  duration?: number;
}

interface ConfirmDialog {
  id: string;
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;
  onConfirm: () => void;
  onCancel: () => void;
}

interface NotificationContextType {
  notifications: Notification[];
  confirmDialog: ConfirmDialog | null;
  showNotification: (notification: Omit<Notification, 'id'>) => void;
  showConfirm: (confirm: Omit<ConfirmDialog, 'id' | 'confirmText' | 'cancelText' | 'onCancel'> & {
    confirmText?: string;
    cancelText?: string;
    onCancel?: () => void;
  }) => Promise<boolean>;
  removeNotification: (id: string) => void;
  closeConfirm: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: React.ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialog | null>(null);

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  const showNotification = useCallback((notification: Omit<Notification, 'id'>) => {
    const id = Date.now().toString();
    const newNotification: Notification = {
      id,
      duration: 5000,
      ...notification,
    };

    setNotifications(prev => [...prev, newNotification]);

    if (newNotification.duration && newNotification.duration > 0) {
      setTimeout(() => {
        removeNotification(id);
      }, newNotification.duration);
    }
  }, [removeNotification]);

  const showConfirm = useCallback((confirm: Omit<ConfirmDialog, 'id' | 'confirmText' | 'cancelText' | 'onCancel'> & {
    confirmText?: string;
    cancelText?: string;
    onCancel?: () => void;
  }): Promise<boolean> => {
    return new Promise((resolve) => {
      const id = Date.now().toString();
      setConfirmDialog({
        id,
        title: confirm.title,
        message: confirm.message,
        confirmText: confirm.confirmText || 'Confirm',
        cancelText: confirm.cancelText || 'Cancel',
        onConfirm: () => {
          confirm.onConfirm();
          setConfirmDialog(null);
          resolve(true);
        },
        onCancel: () => {
          confirm.onCancel?.();
          setConfirmDialog(null);
          resolve(false);
        },
      });
    });
  }, []);

  const closeConfirm = useCallback(() => {
    setConfirmDialog(null);
  }, []);

  return (
    <NotificationContext.Provider value={{
      notifications,
      confirmDialog,
      showNotification,
      showConfirm,
      removeNotification,
      closeConfirm,
    }}>
      {children}
    </NotificationContext.Provider>
  );
};
